import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MailCheck, Trash2, X } from "lucide-react";

export default function AdminMessages() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/messages"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (m) => {
    try { await api.post(`/admin/messages/${m.id}/read`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/admin/messages/${m.id}`); toast.success("Deleted"); setSelected(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-messages-page">
      <h1 className="text-2xl lg:text-3xl font-heading font-semibold text-slate-900">Contact Messages</h1>
      <p className="text-slate-500 mt-1">Messages submitted via the public contact form.</p>

      <div className="mt-8 card-medi overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Status</th>
                <th className="text-left px-6 py-3 font-medium">Name</th>
                <th className="text-left px-6 py-3 font-medium">Email</th>
                <th className="text-left px-6 py-3 font-medium">Subject</th>
                <th className="text-left px-6 py-3 font-medium">Date</th>
                <th className="text-right px-6 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 && <tr><td colSpan={6} className="px-6 py-6 text-slate-500">No messages yet.</td></tr>}
              {items.map((m) => (
                <tr key={m.id} className="hover:bg-slate-50/50 cursor-pointer" onClick={() => setSelected(m)} data-testid={`msg-row-${m.id}`}>
                  <td className="px-6 py-4">
                    {m.read ? <MailCheck className="w-4 h-4 text-red-600" /> : <Mail className="w-4 h-4 text-amber-600" />}
                  </td>
                  <td className="px-6 py-4 font-medium text-slate-800">{m.name}</td>
                  <td className="px-6 py-4 text-slate-600">{m.email}</td>
                  <td className="px-6 py-4 text-slate-600">{m.subject || "—"}</td>
                  <td className="px-6 py-4 text-slate-500">{new Date(m.created_at).toLocaleString()}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={(e) => { e.stopPropagation(); remove(m); }} className="p-2 rounded-lg hover:bg-red-50 text-red-600" data-testid={`msg-delete-${m.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" data-testid="msg-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="font-heading font-semibold text-slate-900">Message details</h3>
              <button onClick={() => setSelected(null)} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-3 text-sm">
              <Row k="From" v={`${selected.name} <${selected.email}>`} />
              {selected.phone && <Row k="Phone" v={selected.phone} />}
              {selected.subject && <Row k="Subject" v={selected.subject} />}
              <Row k="Date" v={new Date(selected.created_at).toLocaleString()} />
              <div>
                <div className="text-xs uppercase tracking-widest text-red-700 font-semibold mb-1">Message</div>
                <p className="text-slate-700 whitespace-pre-line leading-relaxed">{selected.message}</p>
              </div>
              <div className="pt-2 flex gap-3 justify-end">
                {!selected.read && <button onClick={() => { markRead(selected); setSelected({ ...selected, read: true }); }} className="btn-secondary">Mark as read</button>}
                <a href={`mailto:${selected.email}`} className="btn-primary">Reply via Email</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex gap-2"><span className="text-slate-500 w-20">{k}:</span><span className="text-slate-800">{v}</span></div>
);
